<template>
	<div>
		<div class="row">
			<div class="col-12 col-md-3">
				<h2>{{ $t('question-bank-item') }}</h2>
			</div>
			<div class="col-12 col-md-9 text-right">
				<button @click="save" class="btn btn-primary" :disabled="!canSave">{{ examquestion_id ? $t('save-new-version') : $t('save') }}</button>
			</div>
		</div>

		<p v-if="examquestion_id" class="text-muted">{{ $t('used-in-n-forms', { count: formsCount }) }}</p>

		<div v-if="saved">
			<div class="alert alert-success mt-8">{{ $t('saved') }}</div>
		</div>

		<div v-if="error">
			<div class="alert alert-danger mt-8">{{ error }}</div>
		</div>

		<div class="form-group">
			<label for="question-name">{{ $t('name') }}</label>
			<input
				id="question-name"
				class="form-control"
				:class="{ 'is-invalid': !examquestion_id && name && !nameIsValid }"
				v-model="name"
				:placeholder="$t('lowercase-no-spaces')"
				:disabled="!!examquestion_id"
				@blur="normaliseName"
			>
			<small class="form-text text-muted" v-if="examquestion_id">{{ $t('name-locked-once-created') }}</small>
			<small class="form-text text-danger" v-if="!examquestion_id && name && !nameIsValid">{{ $t('name-lowercase-no-spaces-error') }}</small>
		</div>

		<div class="form-group">
			<label>{{ $t('question') }}</label>
			<textarea class="form-control" v-model="question" :placeholder="$t('question')"></textarea>
		</div>

		<div class="form-group">
			<label>{{ $t('description') }}</label>
			<vue-editor v-model="description"></vue-editor>
		</div>

		<div class="form-group">
			<label>{{ $t('info') }}</label>
			<vue-editor v-model="info"></vue-editor>
		</div>

		<div class="form-group">
			<label>{{ $t('type') }}</label>
			<select class="form-control" v-model="examquestiontype">
				<option value="select">{{ $t('grade') }}</option>
				<option value="mark">{{ $t('mark') }}</option>
			</select>
		</div>

		<div class="form-group" v-if="examquestiontype === 'mark'">
			<label>{{ $t('mark-lower-bound') }}</label>
			<input type="number" class="form-control" v-model.number="markmin">
			<label class="mt-2">{{ $t('mark-upper-bound') }}</label>
			<input type="number" class="form-control" v-model.number="markmax">
		</div>

		<div class="form-group" v-if="examquestiontype === 'select'">
			<label>{{ $t('response-value-group') }}</label>
			<div>
				<button class="btn btn-light" @click="openResponseValueGroupSelector">
					{{ responsevaluegroup ? responsevaluegroup.title : $t('choose-response-value-group') }}
				</button>
				<a href="#" class="ml-2" v-if="responsevaluegroup" @click.prevent="unlinkResponseValueGroup">{{ $t('unlink') }}</a>
			</div>
		</div>

		<template v-if="examquestiontype === 'select' && optionRows.length">
			<label>{{ $t('question-options') }}</label>
			<small class="form-text text-muted mb-3">{{ $t('question-options-help') }}</small>

			<div v-for="option in optionRows" :key="option.responsevalue_id" class="card mb-3">
				<div class="card-body">
					<p class="mb-2">
						<strong>{{ option.caption }}</strong>
						<span class="badge badge-secondary ml-2">{{ option.value === -1 ? $t('na') : option.value }}</span>
						<span class="float-right">
							<BtnOnClassPicker :option="option" :okey="option.responsevalue_id"></BtnOnClassPicker>
						</span>
					</p>
					<div class="form-group mb-0">
						<label>{{ $t('description') }}</label>
						<vue-editor v-model="option.description"></vue-editor>
					</div>
				</div>
			</div>
		</template>

		<hr v-if="versions.length">

		<template v-if="versions.length">
			<label>{{ $t('version-history') }}</label>
			<table class="table table-sm">
				<thead>
					<tr>
						<th>{{ $t('version') }}</th>
						<th>{{ $t('question') }}</th>
						<th>{{ $t('created') }}</th>
						<th>{{ $t('responses') }}</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="v in versions" :key="v.examquestionversion_id" :class="{ 'font-weight-bold': v.iscurrent }">
						<td>{{ v.version }}<span v-if="v.iscurrent"> ({{ $t('current') }})</span></td>
						<td>{{ v.question }}</td>
						<td>{{ v.created }}</td>
						<td>{{ v.responsecount }}</td>
					</tr>
				</tbody>
			</table>
		</template>
	</div>
</template>

<script>
import { VueEditor } from "vue2-editor";
import BtnOnClassPicker from "src/components/FormEditor/BtnOnClassPicker";

export default {
	components: {
		VueEditor,
		BtnOnClassPicker
	},
	data() {
		return {
			examquestion_id: null,
			saved: false,
			error: null,
			name: '',
			question: '',
			description: null,
			info: null,
			examquestiontype: 'select',
			markmin: 0,
			markmax: 100,
			formsCount: 0,
			responsevaluegroup_id: null,
			responsevaluegroup: null,
			optionRows: [],
			// responsevalue_id -> this question's own previously-saved description for that
			// option, from the current version's snapshot. Used to pre-fill optionRows when the
			// group is (re)loaded, so descriptions already customised for THIS question survive
			// across edits instead of reverting to the group's shared defaults.
			previousVersionOptions: {},
			versions: []
		};
	},
	computed: {
		nameIsValid() {
			return /^[a-z0-9_-]+$/.test(this.name || '');
		},
		canSave() {
			if (this.examquestiontype === 'mark' && (this.markmin === null || this.markmax === null || this.markmin >= this.markmax)) return false;
			if (this.examquestion_id) return true;
			return this.nameIsValid && !!this.question;
		}
	},
	methods: {
		normaliseName() {
			if (this.examquestion_id) return;
			this.name = (this.name || '').trim().toLowerCase().replace(/\s+/g, '');
		},
		openResponseValueGroupSelector() {
			const self = this;
			this.$cfetch(`${this.$settings.APIBaseURL}/responsevaluegroups/list`, {
				data: { search: '' }
			})
			.then(json => {
				if (json.status === 'SUCCESS') {
					this.$store.commit("openModal", {
						type: "selector",
						title: this.$t('choose-response-value-group'),
						selectCallback: function(selection) {
							self.responsevaluegroup_id = selection.id;
							self.applyResponseValueGroup(selection);
							self.$store.commit("closeModal");
						},
						selected: [],
						data: json.results,
						captionKey: 'title',
						subcaptionKey: 'name',
						idProperty: 'id'
					});
				}
			})
			.catch(error => {
				this.$log.error.call(this, error);
			});
		},
		save() {
			this.normaliseName();
			this.error = null;

			if (!this.canSave) {
				this.error = this.$t('fix-errors-before-saving');
				return;
			}

			const data = {
				id: this.examquestion_id,
				name: this.name,
				question: this.question,
				description: this.description,
				info: this.info,
				examquestiontype: this.examquestiontype,
				responsevaluegroup_id: this.examquestiontype === 'select' ? this.responsevaluegroup_id : null,
				markmin: this.examquestiontype === 'mark' ? this.markmin : null,
				markmax: this.examquestiontype === 'mark' ? this.markmax : null,
				options: this.examquestiontype === 'select' ? this.optionRows.map(({ responsevalue_id, description, cssclass }) => ({ responsevalue_id, description, cssclass })) : []
			};

			this.$cfetch(`${this.$settings.APIBaseURL}/examquestions/save`, {
				method: "post",
				data
			})
			.then(json => {
				if (json.status === 'SUCCESS') {
					this.examquestion_id = json.result.id;
					this.saved = true;
					this.getItem(this.examquestion_id);
					setTimeout(() => {
						this.saved = false;
					}, 5000);
				} else {
					this.error = json.errorMessage || this.$t('unexpected-error-saving');
				}
			})
			.catch(error => {
				this.$log.error.call(this, error);
				this.error = this.$t('unexpected-error-saving');
			});
		},
		getItem(id) {
			this.$cfetch(`${this.$settings.APIBaseURL}/examquestions/getitem/${id}`)
			.then(json => {
				this.$store.commit("hideBlockingSpinner");
				if (json.status === 'SUCCESS') {
					const item = json.result;
					this.examquestion_id = item.id;
					this.name = item.name;
					this.formsCount = item.formscount || 0;
					this.versions = item.versions || [];
					const current = this.versions.find(v => v.iscurrent) || this.versions[this.versions.length - 1];
					if (current) {
						this.question = current.question;
						this.description = current.description;
						this.info = current.info;
						this.examquestiontype = current.examquestiontype_name;
						this.responsevaluegroup_id = current.responsevaluegroup_id;
						this.markmin = current.markmin;
						this.markmax = current.markmax;
						this.previousVersionOptions = {};
						(current.options || []).forEach(option => {
							this.previousVersionOptions[option.responsevalue_id] = option;
						});
						if (this.responsevaluegroup_id) {
							this.loadResponseValueGroup(this.responsevaluegroup_id);
						}
					}
				} else {
					this.error = json.errorMessage || this.$t('unexpected-error-loading');
				}
			})
			.catch(error => {
				this.$log.error.call(this, error);
				this.$store.commit("hideBlockingSpinner");
				this.error = this.$t('unexpected-error-loading');
			});
		},
		loadResponseValueGroup(id) {
			this.$cfetch(`${this.$settings.APIBaseURL}/responsevaluegroups/getitem/${id}`)
			.then(json => {
				if (json.status === 'SUCCESS') {
					this.applyResponseValueGroup(json.result);
				}
			})
			.catch(error => {
				this.$log.error.call(this, error);
			});
		},
		// Rebuilds optionRows from the group's standardised value/caption/ordering, importing
		// this question's own previously-saved description for each option where one exists,
		// and falling back to the group's default description otherwise.
		applyResponseValueGroup(group) {
			this.responsevaluegroup = group;
			this.optionRows = (group.values || []).map(value => {
				const previous = this.previousVersionOptions[value.responsevalue_id];
				return {
					responsevalue_id: value.responsevalue_id,
					// API returns value/ordering as numeric strings - coerce so the N/A
					// check (option.value === -1) and ordering comparisons work correctly.
					value: Number(value.value),
					caption: value.caption,
					ordering: Number(value.ordering),
					description: previous ? previous.description : value.description,
					cssclass: previous ? previous.cssclass : value.cssclass
				};
			});
		},
		unlinkResponseValueGroup() {
			this.responsevaluegroup = null;
			this.responsevaluegroup_id = null;
			this.optionRows = [];
		}
	},
	mounted() {
		if (this.$route.params.examquestionid) {
			this.getItem(this.$route.params.examquestionid);
		} else {
			this.$store.commit("hideBlockingSpinner");
		}
	}
};
</script>
